import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { useChartAxisApi } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useChartAxisApi'
import { DATALAB_MOE_PARAMS } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/marginOfError'

import {
  getAvailableModes,
  getChartModeOptions,
  getDefaultChartMode,
  mapToBuilderKey
} from '@aytm/app/stats/page/question-views/cq/components/partial/lib/chartModes'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'
import ChartModes from '@aytm/app/stats/page/question-views/cq/components/partial/ChartModes'
import Legend from '@aytm/app/stats/page/question-views/cq/components/partial/Legend'

const CorrelationFinderChart = ({
  blockId,
  questionId,
  questionKey,
  qType,
  chartMode: chartModeProp,
  questionChartMode,
  globalChartMode,
  onChartModeChange,
  zoomLevel,
  globalZoomLevel,
  onZoomChange,
  batches,
  text,
  groupType,
  axisType,
  answer,
  answerType,
  format,
  ignoreAmount,
  ignorePercents,
  colorTheme,
  chartLegendMode,
  confidenceLevel,
  significanceLevel,
  confidenceLevels,
  features,
  percentDecimals,
  onChartReady,
  onGroupTypeChange,
  onAxisTypeChange,
  mode,
  legendOpen,
  isReportXpert,
  onLegendClose,
  onViewOptionsClick
}) => {
  const chartRef = useChartAxisApi(onChartReady)

  const chartMode = useMemo(
    () =>
      getDefaultChartMode({
        chartMode: chartModeProp,
        questionChartMode,
        globalChartMode,
        batches,
        ignorePercents,
        groupType
      }),
    [chartModeProp, questionChartMode, globalChartMode, batches, ignorePercents, groupType]
  )

  const chartModeTypes = useMemo(
    () => getAvailableModes({ batches, ignorePercents, groupType }),
    [batches, ignorePercents, groupType]
  )

  const chartModeOptions = useMemo(() => getChartModeOptions(chartModeTypes), [chartModeTypes])

  const builderChartMode = useMemo(() => mapToBuilderKey(chartMode), [chartMode])

  const builderFormat = useMemo(
    () => ({
      ...(format || {}),
      useSigTest: format?.useSigTest !== false,
      percentDecimals: percentDecimals != null ? percentDecimals : 1
    }),
    [format, percentDecimals]
  )

  // Was markup in the question template gated the same way, wired up with jQuery.
  const showModes = format?.modes !== 'hide'

  const zoomType = format?.zoomType !== false && !['stacked', 'bstacked'].includes(chartMode)

  const viewOptionsDisabled = features?.view_options?.disabled

  return (
    <ChartContainer
      chartRef={chartRef}
      questionId={questionId}
      questionKey={questionKey}
      qType={qType}
      chartMode={builderChartMode}
      chartModeTypes={chartModeTypes}
      chartModeOptions={chartModeOptions}
      viewOptionsDisabled={viewOptionsDisabled}
      selectorChartModeValue={chartMode}
      chartModeOverride={chartModeProp}
      globalChartMode={globalChartMode}
      withDefault={false}
      onChartModeChange={onChartModeChange}
      batches={batches}
      text={text}
      groupType={groupType}
      axisType={axisType}
      answer={answer}
      answerType={answerType}
      format={builderFormat}
      linkCells={blockId}
      ignoreAmount={ignoreAmount}
      ignorePercents={ignorePercents}
      ignoreTraits={true}
      ignoreRefs={false}
      zoomType={zoomType}
      zoomLevel={zoomLevel}
      globalZoomLevel={globalZoomLevel}
      onZoomChange={onZoomChange}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      confidenceLevel={confidenceLevel}
      significanceLevel={significanceLevel}
      confidenceLevels={confidenceLevels}
      moeParams={DATALAB_MOE_PARAMS}
      features={features}
    >
      {showModes && (
        <ChartModes
          groupType={groupType}
          axisType={axisType}
          axisTypeDisabled={Boolean(ignoreAmount || ignorePercents)}
          onGroupTypeChange={onGroupTypeChange}
          onAxisTypeChange={onAxisTypeChange}
        />
      )}
      {legendOpen && (
        <Legend
          mode={mode}
          confidenceLevel={confidenceLevel}
          significanceLevel={significanceLevel}
          percentDecimals={percentDecimals}
          isReportXpert={isReportXpert}
          onViewOptionsClick={onViewOptionsClick}
          onClose={onLegendClose}
        />
      )}
    </ChartContainer>
  )
}

CorrelationFinderChart.propTypes = {
  blockId: PropTypes.string,
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  qType: PropTypes.string.isRequired,
  chartMode: PropTypes.string,
  questionChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  onChartModeChange: PropTypes.func.isRequired,
  zoomLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  globalZoomLevel: PropTypes.string,
  onZoomChange: PropTypes.func,
  batches: PropTypes.array.isRequired,
  // the Banner's and the Attribute's question texts: the title reads the first while grouped, the second while interlaced
  text: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  groupType: PropTypes.oneOf(['grouped', 'interlaced']).isRequired,
  axisType: PropTypes.oneOf(['respondents', 'percents']).isRequired,
  answer: answerPropType,
  answerType: PropTypes.string,
  format: PropTypes.object,
  ignoreAmount: PropTypes.bool,
  ignorePercents: PropTypes.bool,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  features: PropTypes.object,
  percentDecimals: PropTypes.number,
  onChartReady: PropTypes.func,
  onGroupTypeChange: PropTypes.func,
  onAxisTypeChange: PropTypes.func,
  mode: PropTypes.oneOf(['sigtest', 'crosstab']),
  legendOpen: PropTypes.bool,
  isReportXpert: PropTypes.bool,
  onLegendClose: PropTypes.func,
  onViewOptionsClick: PropTypes.func
}

CorrelationFinderChart.displayName = 'CorrelationFinderChart'

export default CorrelationFinderChart
